import React, { useState, useEffect } from 'react';
import { AlertCircle, X } from 'lucide-react';

const FeeOverrideModal = ({ isOpen, onClose, recipient, currentFeeConfig, onSave }) => {
  const [config, setConfig] = useState({
    pg_fee_percentage: 2.0,
    gst_percentage: 18.0,
    theraptrack_fee_percentage: 5.0
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentFeeConfig) {
      setConfig({
        pg_fee_percentage: currentFeeConfig.pg_fee_percentage || 2.0,
        gst_percentage: currentFeeConfig.gst_percentage || 18.0,
        theraptrack_fee_percentage: currentFeeConfig.theraptrack_fee_percentage || 5.0
      });
    }
  }, [currentFeeConfig, isOpen]);

  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setConfig(prev => ({ ...prev, [field]: numValue }));
    setError(null);
  };

  const validateConfig = () => {
    const errors = [];
    if (config.pg_fee_percentage < 0 || config.pg_fee_percentage > 100) {
      errors.push('Payment Gateway fee must be between 0-100%');
    }
    if (config.gst_percentage < 0 || config.gst_percentage > 100) {
      errors.push('GST must be between 0-100%');
    }
    if (config.theraptrack_fee_percentage < 0 || config.theraptrack_fee_percentage > 100) {
      errors.push('TheraP Track fee must be between 0-100%');
    }

    const preview = calculatePreview();
    const totalFeePercent = (preview.totalFee / preview.grossAmount) * 100;
    if (totalFeePercent > 50) {
      errors.push(`Warning: Total fees are ${totalFeePercent.toFixed(2)}% of gross amount (>50%)`);
    }

    return errors;
  };

  const handleApplyCustomFees = () => {
    const errors = validateConfig();
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    onSave(config);
    onClose();
  };

  const handleUseCommonFees = () => {
    onSave(null); // null indicates to use common fees
    onClose();
  };

  const calculatePreview = () => {
    if (!recipient) {
      return {
        grossAmount: 0,
        pgFee: 0,
        gstAmount: 0,
        theraptrackFee: 0,
        totalFee: 0,
        netAmount: 0
      };
    }

    const grossAmount = recipient.available_balance || 0;
    const pgFee = (grossAmount * config.pg_fee_percentage) / 100;
    const gstAmount = (pgFee * config.gst_percentage) / 100;
    const theraptrackFee = (grossAmount * config.theraptrack_fee_percentage) / 100;
    const totalFee = pgFee + gstAmount + theraptrackFee;
    const netAmount = grossAmount - totalFee;

    return {
      grossAmount,
      pgFee,
      gstAmount,
      theraptrackFee,
      totalFee,
      netAmount
    };
  };

  if (!recipient || !isOpen) return null;

  const preview = calculatePreview();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Custom Fee Configuration
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Set custom fee percentages for <span className="font-semibold">{recipient.name}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
          {error && (
            <div className="flex items-center space-x-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Recipient Info */}
          <div className="bg-gray-50 p-3 rounded-lg space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Recipient:</span>
              <span className="font-medium">{recipient.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Gross Amount:</span>
              <span className="font-medium">₹{preview.grossAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          {/* Fee Inputs */}
          <div className="grid grid-cols-1 gap-4">
            {/* Payment Gateway Fee */}
            <div className="space-y-2">
              <label htmlFor="modal_pg_fee" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                Payment Gateway Fee (%)
                <span className="text-xs text-gray-500">(on gross amount)</span>
              </label>
              <input
                id="modal_pg_fee"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={config.pg_fee_percentage}
                onChange={(e) => handleInputChange('pg_fee_percentage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-primary dark:text-dark-text-primary"
              />
            </div>

            {/* GST */}
            <div className="space-y-2">
              <label htmlFor="modal_gst" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                GST (%)
                <span className="text-xs text-gray-500">(on PG fee)</span>
              </label>
              <input
                id="modal_gst"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={config.gst_percentage}
                onChange={(e) => handleInputChange('gst_percentage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-primary dark:text-dark-text-primary"
              />
            </div>

            {/* TheraP Track Fee */}
            <div className="space-y-2">
              <label htmlFor="modal_theraptrack_fee" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                TheraP Track Fee (%)
                <span className="text-xs text-gray-500">(on gross amount)</span>
              </label>
              <input
                id="modal_theraptrack_fee"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={config.theraptrack_fee_percentage}
                onChange={(e) => handleInputChange('theraptrack_fee_percentage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-primary dark:text-dark-text-primary"
              />
            </div>
          </div>

          {/* Real-time Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold text-sm text-blue-900">Preview with Custom Fees</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">PG Fee:</span>
                <span className="ml-2 font-medium">₹{preview.pgFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div>
                <span className="text-gray-600">GST:</span>
                <span className="ml-2 font-medium">₹{preview.gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div>
                <span className="text-gray-600">TheraP Track Fee:</span>
                <span className="ml-2 font-medium">₹{preview.theraptrackFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div>
                <span className="text-gray-600">Total Fees:</span>
                <span className="ml-2 font-medium text-red-600">₹{preview.totalFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="col-span-2 pt-2 border-t border-blue-300">
                <span className="text-gray-600">Net Amount:</span>
                <span className="ml-2 font-bold text-green-600 text-lg">₹{preview.netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={handleUseCommonFees}
              className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
            >
              Use Common Fees
            </button>
            <button
              onClick={handleApplyCustomFees}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Apply Custom Fees
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeOverrideModal;
